// Turn a LevelDef into a playable Level on brush collision (BspWorld): shaped pieces, bhop blocks,
// moving, spinning and blinking pieces, launch pads, ladders, water, wind and gravity fields, timer
// zones and one respawn point per stage. Stage zones cover the whole checkpoint
// platform and reach ZONE_HEIGHT above it, so a stage counts even if you jump over the platform.
//
// Plain solids share the world solid; everything with game meaning (blocks, checkpoints, boosters,
// moving pieces) is its own solid, so it can carry its rules and move by offset.

import { BhopBlock } from '../../sim/bhopBlocks';
import { buildBrushGrid, type CollisionData, type SolidInfo } from '../../sim/collision/brushData';
import { BspWorld } from '../../sim/collision/bspWorld';
import type { Box } from '../../sim/collision/boxWorld';
import {
  CONTENTS_LADDER,
  CONTENTS_PLAYERCLIP,
  CONTENTS_SOLID,
  CONTENTS_WATER,
  MASK_PLAYERSOLID,
  type SolidEntity,
} from '../../sim/collision/types';
import { DIST_EPSILON, DT, TICKRATE } from '../../sim/constants';
import { HULL_MAXS_DUCK, HULL_MAXS_STAND, HULL_MINS, type SpawnPoint } from '../../sim/player';
import {
  EV_JUMPED,
  type Level,
  type LevelRuntime,
  type Simulation,
  type Surface,
} from '../../sim/simulation';
import type { Vec3 } from '../../sim/vec';
import type { Zone } from '../../sim/zones';
import { shapePlanes } from './shapes';
import { ZONE_HEIGHT, type LevelBox, type LevelDef } from './types';

/** Stage zones reach this far past their platform's sides, so clipping the edge still counts. */
export const ZONE_MARGIN = 48;

export interface Mover {
  solid: number;
  /** Centre at rest. */
  origin: Vec3;
  offset: Vec3;
  periodTicks: number;
  phase: number;
  /** Current and previous centre (for drawing). */
  pos: Vec3;
  prev: Vec3;
}

/** A piece turning round its centre at a steady rate. */
export interface Spinner {
  solid: number;
  centre: Vec3;
  /** Radians per tick. */
  rate: number;
  yaw: number;
  prevYaw: number;
}

/** A piece that's there for `onTicks`, then gone for `offTicks`. */
export interface Blinker {
  solid: number;
  onTicks: number;
  offTicks: number;
  /** Ticks into the cycle at tick 0. */
  phaseTicks: number;
  on: boolean;
}

/** A wind or gravity field (not solid): what it does to a player inside. */
export interface Field {
  min: Vec3;
  max: Vec3;
  /** Wind: push velocity (Source trigger_push speed times direction). */
  push: Vec3 | null;
  /** Gravity field: gravity scale. */
  gravity: number | null;
}

export interface BuiltBox {
  def: LevelBox;
  /** Solid index in the world (0 = shared static world). */
  solid: number;
  block?: BhopBlock;
  mover?: Mover;
  spinner?: Spinner;
  blinker?: Blinker;
}

/** Pieces that aren't in the collision world (fields are checked against the player's box). */
const NOT_SOLID = new Set(['deco', 'wind', 'gravity']);

/** Contents of a piece's brush: ladders are player clip you can climb, water you swim in. */
function contentsOf(d: LevelBox): number {
  if (d.kind === 'ladder') return CONTENTS_LADDER | CONTENTS_PLAYERCLIP;
  if (d.kind === 'water') return CONTENTS_WATER;
  return CONTENTS_SOLID;
}

export interface BoxLevel extends Level {
  def: LevelDef;
  world: BspWorld;
  boxes: BuiltBox[];
  movers: Mover[];
  startZone: Zone;
  endZone: Zone;
  checkpointZones: Zone[];
  stageSpawns: SpawnPoint[];
  runtime: BoxLevelRuntime;
}

function zoneOver(b: LevelBox, below = 64): Zone {
  return {
    min: [b.min[0] - ZONE_MARGIN, b.min[1] - ZONE_MARGIN, b.max[2] - below],
    max: [b.max[0] + ZONE_MARGIN, b.max[1] + ZONE_MARGIN, b.max[2] + ZONE_HEIGHT],
  };
}

/** Facing for a stage spawn: toward the next piece in the list, else the level's spawn yaw. */
function faceNext(def: LevelDef, i: number): number {
  const b = def.boxes[i];
  const cx = (b.min[0] + b.max[0]) / 2;
  const cy = (b.min[1] + b.max[1]) / 2;
  for (let j = i + 1; j < def.boxes.length; j++) {
    const n = def.boxes[j];
    if (n.kind === 'solid' || n.kind === 'deco') continue;
    const dx = (n.min[0] + n.max[0]) / 2 - cx;
    const dy = (n.min[1] + n.max[1]) / 2 - cy;
    if (Math.hypot(dx, dy) > 1) return Math.atan2(dy, dx);
  }
  return def.spawn.yaw;
}

/** Moving pieces: ping-pong along their offset, carry the player standing on them, push them aside. */
export class BoxLevelRuntime implements LevelRuntime {
  private tick = 0;
  private readonly delta: Vec3 = [0, 0, 0];
  /** Launch velocity of each solid that is a launch pad (null for the rest). */
  private readonly padOf: (Vec3 | null)[] = [];
  /** Anything beyond moving pieces (levels without them run exactly as before). */
  private readonly extras: boolean;

  constructor(
    private readonly world: BspWorld,
    readonly movers: Mover[],
    private readonly boxes: readonly BuiltBox[] = [],
    readonly spinners: Spinner[] = [],
    readonly blinkers: Blinker[] = [],
    private readonly fields: Field[] = [],
  ) {
    for (const b of boxes) if (b.def.kind === 'pad' && b.def.boost) this.padOf[b.solid] = b.def.boost;
    this.extras = spinners.length > 0 || blinkers.length > 0 || fields.length > 0 || this.padOf.length > 0;
  }

  /** The material of the piece at `pos` (moving pieces by their solid). */
  surface(solid: number, pos: Readonly<Vec3>): Surface {
    let hit: LevelBox | undefined;
    for (const b of this.boxes) {
      if (b.def.kind === 'deco') continue;
      if (solid > 0) {
        if (b.solid === solid) hit = b.def;
        continue;
      }
      const { min, max } = b.def;
      if (
        pos[0] >= min[0] - 1 &&
        pos[0] <= max[0] + 1 &&
        pos[1] >= min[1] - 1 &&
        pos[1] <= max[1] + 1 &&
        pos[2] >= min[2] - 1 &&
        pos[2] <= max[2] + 1
      ) {
        hit = b.def;
        break;
      }
    }
    switch (hit?.material) {
      case 'metal':
      case 'neon':
        return 'metal';
      case 'glass':
      case 'ice':
      case 'crystal':
        return 'glass';
      case 'planks':
        return 'wood';
      default:
        return 'concrete';
    }
  }

  private place(m: Mover, tick: number) {
    const s = (1 - Math.cos(2 * Math.PI * (tick / m.periodTicks + m.phase))) / 2;
    for (let k = 0; k < 3; k++) {
      m.pos[k] = m.origin[k] + m.offset[k] * s;
      this.world.offsets[m.solid * 3 + k] = m.pos[k];
    }
  }

  /** Turn a spinner to its yaw at `tick`. */
  private spin(sp: Spinner, tick: number) {
    sp.yaw = sp.rate * tick;
    this.world.setYaw(sp.solid, sp.yaw);
  }

  /** Show or hide a blinker for `tick`. */
  private blink(b: Blinker, tick: number) {
    const cycle = b.onTicks + b.offTicks;
    b.on = (tick + b.phaseTicks) % cycle < b.onTicks;
    this.world.enabled[b.solid] = b.on ? 1 : 0;
  }

  reset() {
    this.tick = 0;
    for (const m of this.movers) {
      this.place(m, 0);
      m.prev[0] = m.pos[0];
      m.prev[1] = m.pos[1];
      m.prev[2] = m.pos[2];
    }
    for (const sp of this.spinners) {
      this.spin(sp, 0);
      sp.prevYaw = sp.yaw;
    }
    for (const b of this.blinkers) this.blink(b, 0);
  }

  beforeMove(sim: Simulation) {
    if (this.extras) this.beforeExtras(sim);
    if (!this.movers.length) return;
    if (!this.extras) this.tick++;
    const p = sim.player;
    const standing = p.ground?.solidIndex ?? -1;
    for (const m of this.movers) {
      m.prev[0] = m.pos[0];
      m.prev[1] = m.pos[1];
      m.prev[2] = m.pos[2];
      this.place(m, this.tick);
      const d = this.delta;
      d[0] = m.pos[0] - m.prev[0];
      d[1] = m.pos[1] - m.prev[1];
      d[2] = m.pos[2] - m.prev[2];
      // Ride along.
      if (standing === m.solid) {
        p.pos[0] += d[0];
        p.pos[1] += d[1];
        p.pos[2] += d[2];
      }
    }
    // Something moved into the player: push them with it.
    const maxs = p.ducked ? HULL_MAXS_DUCK : HULL_MAXS_STAND;
    if (this.world.testPosition(p.pos, HULL_MINS, maxs, MASK_PLAYERSOLID)) {
      for (const m of this.movers) {
        const dx = m.pos[0] - m.prev[0];
        const dy = m.pos[1] - m.prev[1];
        const dz = m.pos[2] - m.prev[2];
        if (dx === 0 && dy === 0 && dz === 0) continue;
        p.pos[0] += dx;
        p.pos[1] += dy;
        p.pos[2] += dz;
        if (!this.world.testPosition(p.pos, HULL_MINS, maxs, MASK_PLAYERSOLID)) break;
        p.pos[0] -= dx;
        p.pos[1] -= dy;
        p.pos[2] -= dz;
      }
    }
  }

  /** Spinners, blinkers and leaving a wind field (before the player moves). Counts the tick. */
  private beforeExtras(sim: Simulation) {
    this.tick++;
    const p = sim.player;
    // Left a wind field: its push becomes real momentum (like Source's trigger_push).
    if (!p.baseVelActive && (p.baseVel[0] !== 0 || p.baseVel[1] !== 0 || p.baseVel[2] !== 0)) {
      const k = 1 + DT * 0.5;
      for (let i = 0; i < 3; i++) p.vel[i] += p.baseVel[i] * k;
      p.baseVel[0] = p.baseVel[1] = p.baseVel[2] = 0;
    }
    p.baseVelActive = false;
    const standing = p.ground?.solidIndex ?? -1;
    for (const sp of this.spinners) {
      sp.prevYaw = sp.yaw;
      this.spin(sp, this.tick);
      // Ride round with it.
      if (standing === sp.solid) {
        const da = sp.yaw - sp.prevYaw;
        const c = Math.cos(da);
        const sn = Math.sin(da);
        const lx = p.pos[0] - sp.centre[0];
        const ly = p.pos[1] - sp.centre[1];
        p.pos[0] = sp.centre[0] + c * lx - sn * ly;
        p.pos[1] = sp.centre[1] + sn * lx + c * ly;
      }
    }
    for (const b of this.blinkers) this.blink(b, this.tick);
  }

  afterMove(sim: Simulation) {
    if (!this.extras) return;
    const p = sim.player;
    // Launch pads: landing on one throws you.
    const g = p.ground?.solidIndex ?? -1;
    const pad = g >= 0 ? this.padOf[g] : null;
    if (pad) {
      p.vel[0] += pad[0];
      p.vel[1] += pad[1];
      p.vel[2] = pad[2];
      p.ground = null;
      sim.events |= EV_JUMPED;
    }
    if (!this.fields.length) return;
    // Fields: the player's box against each (few per level, plain box tests).
    const maxs = p.ducked ? HULL_MAXS_DUCK : HULL_MAXS_STAND;
    let gravity = 1;
    let pushed = false;
    for (const f of this.fields) {
      if (
        p.pos[0] + HULL_MINS[0] > f.max[0] ||
        p.pos[0] + maxs[0] < f.min[0] ||
        p.pos[1] + HULL_MINS[1] > f.max[1] ||
        p.pos[1] + maxs[1] < f.min[1] ||
        p.pos[2] + HULL_MINS[2] > f.max[2] ||
        p.pos[2] + maxs[2] < f.min[2]
      )
        continue;
      if (f.gravity !== null) gravity = f.gravity;
      if (f.push) {
        for (let i = 0; i < 3; i++) p.baseVel[i] = f.push[i] + (pushed ? p.baseVel[i] : 0);
        pushed = true;
      }
    }
    if (pushed) {
      // An upward push lifts you off the ground (Source trigger_push).
      if (p.baseVel[2] > 0 && p.ground) {
        p.ground = null;
        p.pos[2] += 1;
      }
      p.baseVelActive = true;
    }
    p.gravity = gravity;
  }
}

export function buildBoxLevel(def: LevelDef): BoxLevel {
  const planes: number[] = [];
  const brushes: number[] = [];
  const bounds: number[] = [];
  const solids: SolidInfo[] = [{ classname: 'worldspawn', entity: -1, model: 0, origin: [0, 0, 0] }];
  const owned: { i: number; d: LevelBox; solid: number }[] = [];

  def.boxes.forEach((d, i) => {
    if (NOT_SOLID.has(d.kind)) return;
    const sx = d.max[0] - d.min[0];
    const sy = d.max[1] - d.min[1];
    const sz = d.max[2] - d.min[2];
    const c: Vec3 = [(d.min[0] + d.max[0]) / 2, (d.min[1] + d.max[1]) / 2, (d.min[2] + d.max[2]) / 2];
    const own = d.kind !== 'solid' || !!d.move || !!d.spin || !!d.blink;
    let solid = 0;
    if (own) {
      solid = solids.length;
      solids.push({ classname: 'box_' + d.kind, entity: i, model: 0, origin: c });
      owned.push({ i, d, solid });
    }
    const local = shapePlanes(d.shape ?? 'box', sx, sy, sz, d.turn ?? 0);
    const start = planes.length / 4;
    for (let k = 0; k < local.length; k += 4) {
      const [nx, ny, nz, dist] = [local[k], local[k + 1], local[k + 2], local[k + 3]];
      // The shared world solid sits at the origin: move the plane to the piece's centre.
      planes.push(nx, ny, nz, own ? dist : dist + nx * c[0] + ny * c[1] + nz * c[2]);
    }
    brushes.push(start, local.length / 4, contentsOf(d), solid);
    if (own) bounds.push(-sx / 2, -sy / 2, -sz / 2, sx / 2, sy / 2, sz / 2);
    else bounds.push(...d.min, ...d.max);
  });

  const br = new Int32Array(brushes);
  const bd = new Float64Array(bounds);
  const data: CollisionData = {
    planes: new Float64Array(planes),
    brushes: br,
    bounds: bd,
    solids,
    grid: buildBrushGrid(br, bd, solids),
  };
  const world = new BspWorld(data);

  const boxes: BuiltBox[] = [];
  const blocks: BhopBlock[] = [];
  const movers: Mover[] = [];
  const checkpointZones: Zone[] = [];
  const stageSpawns: SpawnPoint[] = [];
  let startZone: Zone | null = null;
  let endZone: Zone | null = null;
  const solidOf = new Map(owned.map((o) => [o.i, o.solid]));
  const spinners: Spinner[] = [];
  const blinkers: Blinker[] = [];
  const fields: Field[] = [];

  def.boxes.forEach((d, i) => {
    if (d.kind === 'wind' || d.kind === 'gravity') {
      fields.push({
        min: [...d.min] as Vec3,
        max: [...d.max] as Vec3,
        push: d.kind === 'wind' && d.boost ? ([...d.boost] as Vec3) : null,
        gravity: d.kind === 'gravity' ? Math.min(3, Math.max(0.1, d.gravity ?? 0.5)) : null,
      });
      return;
    }
    if (d.kind === 'deco') return;
    const solid = solidOf.get(i) ?? 0;
    const built: BuiltBox = { def: d, solid };
    const entity: SolidEntity = world.solids[solid];
    if (d.move && d.move.period > 0) {
      const origin = [...data.solids[solid].origin] as Vec3;
      const m: Mover = {
        solid,
        origin,
        offset: [...d.move.offset] as Vec3,
        periodTicks: Math.max(10, Math.round(d.move.period * TICKRATE)),
        phase: d.move.phase ?? 0,
        pos: [...origin] as Vec3,
        prev: [...origin] as Vec3,
      };
      movers.push(m);
      built.mover = m;
    } else if (d.spin) {
      const sp: Spinner = {
        solid,
        centre: [...data.solids[solid].origin] as Vec3,
        rate: (d.spin * Math.PI) / 180 / TICKRATE,
        yaw: 0,
        prevYaw: 0,
      };
      spinners.push(sp);
      built.spinner = sp;
    }
    if (d.blink && d.blink.on > 0 && d.blink.off > 0) {
      const onTicks = Math.max(1, Math.round(d.blink.on * TICKRATE));
      const offTicks = Math.max(1, Math.round(d.blink.off * TICKRATE));
      const b: Blinker = {
        solid,
        onTicks,
        offTicks,
        phaseTicks: Math.round((d.blink.phase ?? 0) * (onTicks + offTicks)),
        on: true,
      };
      blinkers.push(b);
      built.blinker = b;
    }
    if (d.kind === 'block' && !built.mover) {
      // The drop moves the solid down by offset; the block rule reads the box below.
      const box: Box = { min: [...d.min] as Vec3, max: [...d.max] as Vec3, entity };
      const blk = new BhopBlock(box);
      const z0 = world.offsets[solid * 3 + 2];
      blk.onOffset = (o) => (world.offsets[solid * 3 + 2] = z0 + o);
      built.block = blk;
      entity.block = blk;
      blocks.push(blk);
    } else if (d.kind === 'boost' && d.boost) {
      entity.boost = [...d.boost] as Vec3;
    } else if (d.kind === 'start') {
      startZone ??= {
        min: [d.min[0], d.min[1], d.max[2] - 64],
        max: [d.max[0], d.max[1], d.max[2] + ZONE_HEIGHT],
      };
    } else if (d.kind === 'end') {
      endZone ??= zoneOver(d);
    } else if (d.kind === 'checkpoint') {
      const stage = checkpointZones.length + 2;
      checkpointZones.push(zoneOver(d));
      const spawn: SpawnPoint = {
        pos: [(d.min[0] + d.max[0]) / 2, (d.min[1] + d.max[1]) / 2, d.max[2] + DIST_EPSILON],
        yaw: faceNext(def, i),
        stage,
      };
      stageSpawns.push(spawn);
      // Landing on it also sets the respawn, like the built-in course's checkpoints.
      entity.checkpoint = spawn;
    }
    boxes.push(built);
  });
  if (!startZone || !endZone) throw new Error('A level needs a start and an end platform.');
  if (movers.length || spinners.length) world.setDynamic([...movers, ...spinners].map((m) => m.solid));

  const runtime = new BoxLevelRuntime(world, movers, boxes, spinners, blinkers, fields);
  const spawn: SpawnPoint = { pos: [...def.spawn.pos] as Vec3, yaw: def.spawn.yaw, stage: 1 };
  return {
    def,
    world,
    boxes,
    movers,
    blocks,
    spawn,
    startZone,
    endZone,
    checkpointZones,
    stageSpawns,
    stageCount: checkpointZones.length + 1,
    failZ: def.failZ,
    runtime,
  };
}
